import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuthToken, clearTokens, refreshAuthToken } from '../utils/auth';
import { checkConsoleAccess } from '../utils/consoleAccess';
import { toast } from 'react-toastify';

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Reactive token refresh — matches the Flutter app pattern.
 *
 * No proactive timer, no visibilitychange hook. We simply let every request
 * go out with whatever auth token we have. If the backend returns 401, the
 * fetch interceptor below (and fetchWithAuth in utils/auth.ts) transparently
 * hits the refresh endpoint, updates BOTH tokens, and retries the original
 * request. Only an explicit 401/403 from the refresh endpoint itself causes
 * a logout.
 */
const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasLoggedOut = useRef(false);
  const accessChecked = useRef(false);

  const handleLogout = useCallback((message: string) => {
    if (hasLoggedOut.current) return;
    hasLoggedOut.current = true;
    accessChecked.current = false;
    toast.error(message);
    clearTokens();
    navigate('/');
    setTimeout(() => {
      hasLoggedOut.current = false;
    }, 1000);
  }, [navigate]);

  useEffect(() => {
    const authToken = getAuthToken();

    // On the login page, if we already have a token, jump to dashboard.
    // If the token is actually invalid, the first API call will hit 401 and
    // the interceptor will refresh or log out.
    if (location.pathname === '/') {
      if (authToken) navigate('/dashboard');
      return;
    }

    if (!authToken) {
      handleLogout('Please log in to continue');
      return;
    }

    // Global fetch interceptor: on 401, refresh and retry transparently.
    // This is the reactive pattern — we do nothing until the backend tells
    // us the token is bad.
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      // Skip interceptor on the login page
      if (location.pathname === '/') return response;

      // Skip interceptor for refresh endpoint itself (avoid infinite loop)
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';
      if (url.includes('refreshTokenV1')) return response;

      // A valid token that is simply not a moderator's: a refresh cannot change that and would only rotate the token.
      if (response.status === 403) {
        try {
          if ((await response.clone().json())?.code === 'MODERATOR_REQUIRED') return response;
        } catch {
          // not JSON: not the moderator gate's answer
        }
      }

      // On 401 OR 403: refresh tokens and retry the original request once.
      // AWS API Gateway returns 403 ("explicit deny in identity-based policy")
      // when an expired/invalid token is rejected at the IAM layer, so we
      // must treat it the same as 401 (matches Flutter app interceptor).
      if (response.status === 401 || response.status === 403) {
        const newToken = await refreshAuthToken();
        if (newToken) {
          const [input, init] = args;
          const retryInit = { ...(init || {}) };
          const headers = new Headers(retryInit.headers || {});
          headers.set('Authorization', newToken);
          retryInit.headers = headers;
          return originalFetch(input, retryInit);
        }
        // Refresh returned null. If it was a definitive 401/403 from the
        // refresh endpoint, tokens are already cleared — log the user out.
        // If it was a transient failure, tokens are still there; let the
        // user see the error but stay logged in.
        if (!getAuthToken()) {
          handleLogout('Session expired. Please log in again.');
        }
        return response;
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [navigate, location.pathname, handleLogout]);

  // A session opened before the moderator list changed must not outlive it: ask the server once per page load.
  // Only its explicit "not a moderator" signs out; an outage or a refresh problem does not.
  useEffect(() => {
    if (accessChecked.current || location.pathname === '/' || !getAuthToken()) return;
    accessChecked.current = true;
    checkConsoleAccess().then((access) => {
      if (access === 'denied') handleLogout('Access denied: You are not authorized to use this system.');
    });
  }, [location.pathname, handleLogout]);

  return <>{children}</>;
};

export default AuthProvider;
